import { Link } from "@tanstack/react-router";
import type { HubEditorial as HubEditorialData } from "@/data/editorial";
import { sourcesFor, sourcesByUrl } from "@/data/sources";
import { ensurePresumptionNotice } from "@/lib/editorial-compliance";
import { SourceList } from "@/components/SourceList";
import { ReliabilityBadge } from "@/components/ReliabilityBadge";

/** Bloc éditorial original + FAQ affichés sur les hubs (thèmes, lieux, années). */
export function HubEditorial({
  data,
  sourceKey,
  hideSources = false,
}: {
  data: HubEditorialData;
  /** Clé de références (ex. "personnalite/jacques-mariani"). */
  sourceKey?: string;
  /** Masque le bloc sources quand la page l'affiche ailleurs. */
  hideSources?: boolean;
}) {
  return (
    <section className="mt-16 border-t border-border pt-8">
      <h2 className="font-[family-name:var(--font-display)] text-2xl leading-tight md:text-3xl">
        {data.heading}
      </h2>
      {hideSources ? null : <ReliabilityBadge sourceKey={sourceKey} className="mt-4" />}
      <div className="mt-5 space-y-4 text-[0.95rem] leading-relaxed text-muted-foreground">
        {data.paragraphs.map((p, i) => (
          <p key={i}>{ensurePresumptionNotice(p)}</p>
        ))}
      </div>

      {data.verbatims?.length ? (
        <>
          <h2 className="mt-12 border-b border-foreground pb-3 text-[0.65rem] uppercase tracking-[0.35em]">
            Ce que montre chaque document
          </h2>
          <ul className="mt-4 divide-y divide-border">
            {data.verbatims.map((v) => (
              <li key={v.id} className="py-3">
                <Link
                  to="/video/$slug"
                  params={{ slug: v.id }}
                  className="text-base font-medium underline-offset-4 hover:underline"
                >
                  {v.title}
                </Link>
                <p className="mt-1 text-[0.95rem] leading-relaxed text-muted-foreground">
                  {ensurePresumptionNotice(v.note)}
                </p>
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {data.pillars?.length ? (
        <>
          <h2 className="mt-12 border-b border-foreground pb-3 text-[0.65rem] uppercase tracking-[0.35em]">
            Poursuivre sur les pages de référence
          </h2>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {data.pillars.map((p) => (
              <li key={p.to}>
                <Link
                  to={p.to as never}
                  className="block h-full rounded-xl border border-border p-4 transition-colors hover:border-foreground"
                >
                  <span className="block font-medium">{p.label}</span>
                  <span className="mt-1 block text-sm text-muted-foreground">{p.sub}</span>
                </Link>
              </li>
            ))}
          </ul>
        </>
      ) : null}



      <h2 className="mt-12 border-b border-foreground pb-3 text-[0.65rem] uppercase tracking-[0.35em]">
        Questions fréquentes
      </h2>
      <div className="mt-4 divide-y divide-border">
        {data.faq.map((f, i) => (
          <details key={i} className="group py-4" open={i === 0}>
            <summary className="cursor-pointer list-none text-base font-medium marker:hidden">
              <span className="mr-2 text-muted-foreground group-open:hidden">+</span>
              <span className="mr-2 hidden text-muted-foreground group-open:inline">−</span>
              {f.q}
            </summary>
            <p className="mt-3 pl-5 text-[0.95rem] leading-relaxed text-muted-foreground">
              {ensurePresumptionNotice(f.a)}
            </p>
            {sourcesByUrl(f.refs).length > 0 ? (
              <p className="mt-2 pl-5 text-xs text-muted-foreground">
                <span className="uppercase tracking-[0.2em]">Sources</span>{" "}
                {sourcesByUrl(f.refs).map((s, j) => (
                  <span key={s.url}>
                    {j > 0 ? " · " : ""}
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="text-foreground underline underline-offset-4 hover:no-underline"
                    >
                      {s.publisher}
                      {s.date ? `, ${s.date}` : ""}
                    </a>
                  </span>
                ))}
              </p>
            ) : null}
          </details>
        ))}
      </div>

      {hideSources ? null : <SourceList sources={sourcesFor(sourceKey)} sourceKey={sourceKey} />}
    </section>
  );
}